import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import isEqual from 'lodash/isEqual'
import { useSelector } from "react-redux";
import { selectQuickWidgetConfig, selectQuickWidgets } from "../../selectors/quickWidgets";
import useDraggable, { Position } from "../../hooks/useDraggable";
import { alpha, Box, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Paper, Popover, Stack, Typography, useTheme } from '@mui/material'
import { useStyles } from './styles'
import { makeSelectQuickModeEnabled } from "../../selectors/user";
import { useDispatch } from "react-redux";
import { QuickWidgetTypes, updateQuickWidgetConfig } from "../../reducer/user.reducer";
import CloseIcon from '@mui/icons-material/Close'
import useResizeable from "../../hooks/useResizeable";
import SettingsOutlinedIcon from "../../assets/icons/SettingOutlined";
import { setOpenQuickTradeSettingsDialog } from "../../reducer/quickTrades.reducer";
import { clampQuickWidgetBounds, QuickWidgetPositionInput, resolveQuickWidgetTop } from "../../util/quickWidgets";

export type QuickWidgetDialogProps = {
    title: React.ReactNode,
    widgetType: QuickWidgetTypes,
    onDragStart?: (widgetType: QuickWidgetTypes) => void
    onDragEnd?: (widgetType: QuickWidgetTypes) => void
}

export type WidgetStyle = {
    left: number,
    top: number,
    height: number,
    width: number
}

const QuickWidgetDialog: React.FC<PropsWithChildren<QuickWidgetDialogProps>> = ({ title, widgetType, children }) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const { classes, cx } = useStyles();
    const quickWidgets = useSelector(selectQuickWidgets)
    const widgetConfig = useSelector(selectQuickWidgetConfig(widgetType));
    const { position: widgetPosition, dimensions } = widgetConfig!
    const QuickWidgetMode = useSelector(makeSelectQuickModeEnabled(widgetType))

    const [persistedStyle, setPersistedStyle] = useState<WidgetStyle>(() => ({
        left: widgetPosition?.left ?? 0,
        top: resolveQuickWidgetTop(widgetPosition as QuickWidgetPositionInput | undefined, dimensions.height),
        ...dimensions
    }))
    const [resizePreview, setResizePreview] = useState<WidgetStyle | null>(null)
    const [viewportSize, setViewportSize] = useState(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
    }))
    const [displayOrder, setDisplayOrder] = useState(widgetConfig?.order ?? 0)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const persistedStyleRef = useRef(persistedStyle)
    const resizeBaseStyleRef = useRef<WidgetStyle | null>(null)
    const prevIsResizingRef = useRef(false)
    const blockReduxSyncRef = useRef(false)

    persistedStyleRef.current = persistedStyle

    const activeStyle = resizePreview ?? persistedStyle
    const displayStyle = useMemo(
        () => clampQuickWidgetBounds(activeStyle, viewportSize.width, viewportSize.height),
        [activeStyle, viewportSize]
    )

    const bringWidgetToFront = useCallback(() => {
        const widget = quickWidgets?.[widgetType];
        if (!widget) return;

        const currentMaxOrder = Math.max(
            0,
            ...Object.values(quickWidgets || {}).map((w) => w?.order || 0)
        );
        if (widget.order === currentMaxOrder) {
            let occurences = 0
            Object.values(quickWidgets || {}).forEach(w => {
                if (w?.order === widget.order) occurences++
            })
            if (occurences === 1) {
                setDisplayOrder(widget.order)
                return;
            }
        }

        const newOrder = currentMaxOrder + 1;
        setDisplayOrder(newOrder);

        dispatch(updateQuickWidgetConfig({
            widgetType,
            config: { order: newOrder },
        }));
    }, [dispatch, quickWidgets, widgetType]);

    const handleDragEnd = useCallback((finalPosition: Position) => {
        blockReduxSyncRef.current = true
        flushSync(() => {
            setPersistedStyle(prev => ({
                ...prev,
                left: finalPosition.left,
                top: finalPosition.top,
            }))
        })
    }, []);

    const { containerRef, handleRef, isDragging } = useDraggable<HTMLDivElement, any>(
        document.body,
        bringWidgetToFront,
        handleDragEnd
    )
    const { containerRef: resizeContainerRef, handleRef: resizeHandleRef,
        isResizing, originalDimensions, delta } = useResizeable<HTMLDivElement, HTMLDivElement>(200, 42)

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const previousStyle = useRef<WidgetStyle | undefined>(undefined);

    useEffect(() => {
        if (widgetConfig?.order != null) {
            setDisplayOrder(widgetConfig.order)
        }
    }, [widgetConfig?.order])

    useEffect(() => {
        if (isDragging || isResizing) return;

        const nextStyle: WidgetStyle = {
            left: widgetPosition?.left ?? 0,
            top: resolveQuickWidgetTop(widgetPosition as QuickWidgetPositionInput | undefined, dimensions.height),
            ...dimensions
        }

        if (blockReduxSyncRef.current) {
            if (isEqual(nextStyle, persistedStyleRef.current)) {
                blockReduxSyncRef.current = false
            }
            return
        }

        setPersistedStyle(prev => isEqual(prev, nextStyle) ? prev : nextStyle)
    }, [widgetPosition, dimensions, isDragging, isResizing])

    useEffect(() => {
        if (isResizing && originalDimensions && !resizeBaseStyleRef.current) {
            resizeBaseStyleRef.current = { ...persistedStyleRef.current }
        }

        if (isResizing && delta && originalDimensions) {
            const base = resizeBaseStyleRef.current ?? persistedStyleRef.current
            setResizePreview({
                left: base.left,
                top: base.top,
                height: originalDimensions.height + delta.y,
                width: originalDimensions.width + delta.x,
            })
        }

        if (prevIsResizingRef.current && !isResizing) {
            setResizePreview(prev => {
                if (prev) {
                    blockReduxSyncRef.current = true
                    setPersistedStyle(prev)
                }
                return null
            })
            resizeBaseStyleRef.current = null
        }

        prevIsResizingRef.current = isResizing
    }, [isResizing, delta, originalDimensions])

    useEffect(() => {
        const handleResize = () => {
            setViewportSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (isDragging || isResizing) return;
        if (previousStyle.current && isEqual(previousStyle.current, persistedStyle)) return;

        const timeout = window.setTimeout(() => {
            const style = persistedStyleRef.current
            dispatch(updateQuickWidgetConfig({
                widgetType,
                config: {
                    position: {
                        left: style.left,
                        top: style.top
                    },
                    dimensions: {
                        width: style.width,
                        height: style.height
                    }, QuickmodeEnabled: false

                }
            }))
            previousStyle.current = style;
        }, 100)

        return () => {
            clearTimeout(timeout)
        }
    }, [widgetType, persistedStyle, isDragging, isResizing, dispatch])

    const onCloseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(updateQuickWidgetConfig({
            widgetType,
            config: { enabled: false },
        }));
    }, [widgetType, dispatch]);

    const onWidgetMouseDown = useCallback((e: React.MouseEvent) => {
        if (handleRef.current?.contains(e.target as Node)) return;
        bringWidgetToFront();
    }, [bringWidgetToFront, handleRef]);

    const sxProps = useMemo(
        () => ({
            ...displayStyle,
            zIndex: displayOrder,
            border: 1,
            borderColor: 'divider',
        }),
        [displayStyle, displayOrder]
    )

    const handleQuickModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        dispatch(updateQuickWidgetConfig({
            widgetType,
            config: {
                QuickmodeEnabled: event.target.checked,
            },
        }));
    };
    const handleQuickModeToggle = (event: React.MouseEvent<HTMLElement>) => {
        if(widgetType === QuickWidgetTypes.QUICK_TRADE) {
            dispatch(setOpenQuickTradeSettingsDialog({
                settingsDialogOpen: true,
                dialogMode: 'view',
                editTemplateId: undefined
            }))
            handleClose()
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    return <Paper ref={containerRef} sx={sxProps} className={classes.container} elevation={5} onMouseDown={onWidgetMouseDown}>
        <div className={classes.resizeContainer} ref={resizeContainerRef}>
            <Stack direction="column" sx={{height: '100%'}}>
                <Paper ref={handleRef} className={cx(classes.heading, { [classes.dragging]: isDragging })} sx={{
                    width: '100%',
                    padding: 0.5,
                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.background.default : alpha(theme.palette.background.default, 0.1),

                }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography fontSize={widgetType === QuickWidgetTypes.MARKET_DEPTH ? 12 : 14}>
                            {title as any}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2} flexShrink={0} minWidth="40px">
                            {[QuickWidgetTypes.ORDER, QuickWidgetTypes.POSITION, QuickWidgetTypes.QUICK_TRADE].includes(widgetType) ? (
                                <>
                                    <IconButton
                                        size="small"
                                        className={classes.settingsButton}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={handleQuickModeToggle}
                                        sx={{ p: 0.5 }}
                                    >
                                        <SettingsOutlinedIcon style={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <Popover
                                        open={open}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        style={{ zIndex: 9999999 }}
                                    >
                                        <MenuItem>
                                            <FormControlLabel
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontSize: '13px',
                                                    margin: 0,
                                                }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={QuickWidgetMode}
                                                        onChange={handleQuickModeChange}
                                                    />
                                                }
                                                label={
                                                    <Box component="span" sx={{ fontSize: '13px', lineHeight: 1 }}>
                                                        Enable Quick Trade Mode
                                                    </Box>
                                                }
                                            />
                                        </MenuItem>
                                    </Popover>
                                </>) : null}
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={onCloseClick}
                                className={classes.closeButton}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>
                <Divider orientation="horizontal" />
                <Paper className={classes.content} sx={{ bgcolor: 'background.paper' }}>
                    {children as any}
                </Paper>
            </Stack>
            <div ref={resizeHandleRef} className={classes.resizeHandle} />
        </div>
    </Paper>;
}

export default memo(QuickWidgetDialog);